// @file store for plans (pricing, quotas) and user actions to update them
import type { User } from '@@/types'
import type { PlansState, UpgradeSource } from '@@/vuexstore/modules/plans'
import { blankState } from '@@/vuexstore/modules/plans'
import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, ref } from 'vue'
import type { Store } from 'vuex'

export const usePersonalPlansStore = defineStore('personalPlans', () => {
  const vuexStore = ref<Store<any> | undefined>(undefined)

  const { plans, plansFetched, fetchPlans } = usePlans()
  const { advertisedQuotas, isLoadingAdvertisedQuotas, fetchAdvertisedQuotas } = useQuotas()

  // The state here is used by a root component and getters would be called before the vuex store is available on the window object.
  // We would need to rely on this method to populate it instead of directly called getVuexStore.
  const initialize = (options: { vuexStore: Store<any> | undefined }): void => {
    vuexStore.value = options.vuexStore
  }

  function usePlans(): {
    plans: ComputedRef<PlansState['plans']>
    plansFetched: ComputedRef<boolean>
    fetchPlans: () => void
  } {
    const plans = computed<PlansState['plans']>(() => vuexStore.value?.getters['plans/plans'] ?? blankState().plans)

    const plansFetched = computed<boolean>(() => vuexStore.value?.getters['plans/plansFetched'] ?? false)

    const fetchPlans = (): void => {
      void vuexStore.value?.dispatch('plans/fetchPlans')
    }
    return { plans, plansFetched, fetchPlans }
  }

  function useQuotas(): {
    advertisedQuotas: ComputedRef<PlansState['advertisedQuotas']>
    isLoadingAdvertisedQuotas: ComputedRef<boolean>
    fetchAdvertisedQuotas: () => void
  } {
    const advertisedQuotas = computed<PlansState['advertisedQuotas']>(
      () => vuexStore.value?.getters['plans/advertisedQuotas'] ?? blankState().advertisedQuotas,
    )

    const isLoadingAdvertisedQuotas = computed<boolean>(
      () => vuexStore.value?.getters['plans/isLoadingAdvertisedQuotas'] ?? false,
    )

    const fetchAdvertisedQuotas = (): void => {
      void vuexStore.value?.dispatch('plans/fetchAdvertisedQuotas')
    }

    return { advertisedQuotas, isLoadingAdvertisedQuotas, fetchAdvertisedQuotas }
  }

  const setPlanUpgradeSource = (upgradeSource: UpgradeSource): void => {
    void vuexStore.value?.dispatch('plans/setUpgradeSource', { upgradeSource })
  }

  const userTriggeredUpgrade = (payload: { user: User; upgradeSource: UpgradeSource }): void => {
    void vuexStore.value?.dispatch('plans/userTriggeredUpgrade', payload, { root: true })
  }

  return {
    initialize,
    plans,
    plansFetched,
    fetchPlans,
    advertisedQuotas,
    isLoadingAdvertisedQuotas,
    fetchAdvertisedQuotas,
    setPlanUpgradeSource,
    userTriggeredUpgrade,
  }
})
